import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./Homepage.css";
import {
  getCountries,
  getTopDestinations,
  getActiveAds,
  getLebanonCities
} from "../../api";
import { useNavigate } from "react-router-dom";
import { FaRegCopy, FaWhatsapp } from "react-icons/fa";

const FALLBACK_COUNTRIES = [
  { id: "LB", name: "Lebanon", ads: 124, flag: null },
  { id: "SA", name: "Saudi Arabia", ads: 430, flag: null },
  { id: "EG", name: "Egypt", ads: 512, flag: null },
  { id: "AE", name: "UAE", ads: 301, flag: null },
];

const FALLBACK_CATEGORIES = [
  { id: "restaurants", title: "Restaurants", icon: null },
  { id: "hotels", title: "Hotels", icon: null },
  { id: "cars", title: "Cars", icon: null },
  { id: "fashion", title: "Fashion", icon: null },
  { id: "services", title: "Services", icon: null },
  { id: "realestate", title: "Real Estate", icon: null },
];

const SAMPLE_FEATURED = [
  { title: "Luxury Suite Downtown Beirut", price: "$120/night", snippet: "Elegant suite with sea view" },
  { title: "Toyota Corolla 2018", price: "$8,500", snippet: "Well maintained, single owner" },
  { title: "Restaurant - For Sale", price: "Contact", snippet: "Prime location in Jounieh" },
  { title: "Designer Dresses", price: "$60", snippet: "New collection" },
  { title: "Apartment for Rent in Hamra", price: "$700/month", snippet: "2 bed, 1 bath" },
  { title: "Used iPhone 12", price: "$400", snippet: "Good condition, 128GB" },
  { title: "Wedding Photography Services", price: "Contact", snippet: "Professional and affordable" },
  { title: "Mountain Bike", price: "$300", snippet: "Like new, rarely used" },
  { title: "Office Space for Rent", price: "$1,200/month", snippet: "Downtown location" },
  { title: "Catering Services", price: "Contact", snippet: "For all occasions" },
];

function bufferToBase64(buffer) {
  if (!buffer || !Array.isArray(buffer)) return null;
  const binary = String.fromCharCode(...buffer);
  return `data:image/png;base64,${btoa(binary)}`;
}

const DEFAULT_AD_IMAGE = "/images/default-ad.png";

const HomePage = () => {
  const { t, i18n } = useTranslation();

  const [countries, setCountries] = useState(FALLBACK_COUNTRIES);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [featuredAds, setFeaturedAds] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [videoPlaying, setVideoPlaying] = useState(true);
  const [videoMuted, setVideoMuted] = useState(false);
  const videoRef = useRef(null);
  const heroRef = useRef(null);

  const navigate = useNavigate();

  // -------------------------
  // FETCH DATA
  // -------------------------
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [countriesRes, categoriesRes, adsRes] = await Promise.all([
          getCountries(),
          getTopDestinations(),
          getActiveAds({ page: 1, pageSize: 32 })
        ]);

        // Countries
        if (Array.isArray(countriesRes)) {
          setCountries(
            countriesRes.map((c) => ({
              id: c.CountryID || c.id || "",
              name: c.CountryName || c.name || "Unknown",
              flag: c.Flag?.data ? bufferToBase64(c.Flag.data) : null,
              ads: c.activePlaces ?? 0
            }))
          );
        }

        // Categories
        if (Array.isArray(categoriesRes)) {
          setCategories(
            categoriesRes.map((cat) => ({
              id: cat.CategoryID || cat.id || "",
              title: cat.NameCategory || cat.CategoryName || cat.title,
              icon: cat.Icon || null,
            }))
          );
        }

        // Ads (normalize structure)
        if (adsRes?.ads?.length) {
          const normalizedAds = adsRes.ads.map((ad) => ({
            id: ad.AdID || ad.id,
            title: ad.Title || ad.title,
            snippet: ad.Snippet || ad.snippet,
            price: ad.Price || ad.price,
            imageUrl: ad.image_url || null,
            phone: ad.Phone || ad.phone || null,
          }));
          setFeaturedAds(normalizedAds);
        }

      } catch (err) {
        console.error(err);
        setError(t("Home Error Fallback"));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [t]);

  // -------------------------
  // AUTO SCROLL CARDS (Desktop only)
  // -------------------------
  useEffect(() => {
    if (window.innerWidth <= 600) return; // Disable auto-scroll on mobile

    const track = document.querySelector(".featured-track");
    if (!track) return;

    const isRTL = document.dir === "rtl";
    let direction = 1;
    let animationFrame;

    function autoScroll() {
      if (isRTL) {
        track.scrollLeft -= direction * 1.2;
        if (track.scrollLeft <= 0) direction = -1;
        if (track.scrollLeft >= track.scrollWidth - track.clientWidth) direction = 1;
      } else {
        track.scrollLeft += direction * 1.2;
        if (track.scrollLeft >= track.scrollWidth - track.clientWidth) direction = -1;
        if (track.scrollLeft <= 0) direction = 1;
      }

      animationFrame = requestAnimationFrame(autoScroll);
    }

    // Start
    animationFrame = requestAnimationFrame(autoScroll);

    // Stop when user touches
    const stop = () => cancelAnimationFrame(animationFrame);
    const resume = () => (animationFrame = requestAnimationFrame(autoScroll));

    track.addEventListener("touchstart", stop);
    track.addEventListener("touchend", resume);
    track.addEventListener("mousedown", stop);
    track.addEventListener("mouseup", resume);

    return () => {
      cancelAnimationFrame(animationFrame);
      track.removeEventListener("touchstart", stop);
      track.removeEventListener("touchend", resume);
      track.removeEventListener("mousedown", stop);
      track.removeEventListener("mouseup", resume);
    };
  }, [featuredAds]);

  // -------------------------
  // MOBILE SLIDER DOTS + SWIPE
  // -------------------------
  useEffect(() => {
    if (window.innerWidth > 600) return; // Only for mobile

    const track = document.getElementById("featuredTrack");
    const dotsBox = document.getElementById("sliderDots");
    if (!track || !dotsBox) return;

    // Remove any existing dots
    dotsBox.innerHTML = "";

    // Add a dot for each card
    [...track.children].forEach((_, i) => {
      const dot = document.createElement("span");
      if (i === 0) dot.classList.add("active");
      dotsBox.appendChild(dot);
    });

    const dots = dotsBox.children;

    // Update active dot on scroll
    const onScroll = () => {
      const cardWidth = track.children[0]?.clientWidth || 1;
      const scrollLeft = Math.abs(track.scrollLeft); // RTL support
      const index = Math.round(scrollLeft / cardWidth);
      [...dots].forEach(d => d.classList.remove("active"));
      dots[index]?.classList.add("active");
    };

    track.addEventListener("scroll", onScroll);
    return () => track.removeEventListener("scroll", onScroll);
  }, [featuredAds]);

  // -------------------------
  // VIDEO: Pause/mute on scroll, resume/unmute on view, manual controls
  // -------------------------
  useEffect(() => {
    const handleScroll = () => {
      if (!videoRef.current || !heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      const inView = rect.bottom > 0 && rect.top < window.innerHeight;
      if (inView) {
        if (videoRef.current.paused && videoPlaying) {
          videoRef.current.play();
        }
        if (!videoMuted) {
          videoRef.current.muted = false;
        }
      } else {
        if (!videoRef.current.paused) {
          videoRef.current.pause();
        }
        videoRef.current.muted = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line
  }, [videoPlaying, videoMuted]);

  const handleVideoToggle = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setVideoPlaying(true);
      if (!videoMuted) videoRef.current.muted = false;
    } else {
      videoRef.current.pause();
      setVideoPlaying(false);
    }
  };

  const handleMuteToggle = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setVideoMuted(videoRef.current.muted);
  };

  // -------------------------
  // HANDLERS
  // -------------------------
  const handleSeeAllCountries = () => navigate("/countries");
  const handleSeeAllCategories = () => navigate("/categories");
  const handleSeeAllAds = () => navigate("/ads");

  const openAdDetails = (ad) => {
    if (ad.id) {
      navigate(`/ads/${ad.id}`);
    } else {
      // Fallback if SAMPLE_FEATURED item is clicked
      navigate("/ads");
    }
  };

  // Split featured ads for two sections
  const allAds = featuredAds.length ? featuredAds : SAMPLE_FEATURED;
  const firstFourAds = allAds.slice(0, 18);
  const remainingAds = allAds.slice(18, 32);

  // Split categories for "Top category" and "اعلانات مبوبة"
  const topCategories = categories.slice(0, 3);
  const bombedCategories = categories.slice(3);

  // --- CATEGORY IMAGE RENDERING LOGIC (AllDestinations style) ---
  const renderCategoryCard = (cat) => (
    <div
      key={cat.id}
      className="cat-card"
      onClick={() => navigate(`/category/${cat.id}`)}
      tabIndex={0}
      role="button"
      aria-label={cat.title}
    >
      <div
        className="cat-image"
        style={{
          backgroundImage: cat.icon
            ? `url(${cat.icon})`
            : "linear-gradient(135deg, #f2f2f2 60%, #e0e0e0 100%)"
        }}
      >
        <div className="cat-overlay">
          {/* <h2>{cat.title}</h2> */}
        </div>
      </div>
      <h3 className="cat-name">{cat.title}</h3>
    </div>
  );

  return (
    <main className="homepage-container page container">

      {loading && <p className="muted">{t("Home Loading")}</p>}
      {error && <p className="muted" style={{ color: "red" }}>{error}</p>}
      {/* HERO BANNER */}
      {/* ================= HERO BANNER ================= */}
      <section className="homepage-hero-banner" ref={heroRef}>
        <div className="hero-featured-badge">
          {t("Featured Ad")}
        </div>
        <div className="hero-row">

          {/* TEXT / STORY */}
          <div className="hero-copy">
            <span className="hero-badge">
              🍯 {t("Honey Badge")}
            </span>

            <h1 className="hero-title">
              {t("Honey Title")}
            </h1>

            <p className="hero-desc">
              {t("Honey Description")}
            </p>

            {/* BRAND + WHATSAPP */}
            <div className="hero-contact">
              <strong className="hero-brand">
                🍯 Honey by Khadija
              </strong>

              <a
                href="https://wa.me/9613239467"
                target="_blank"
                rel="noopener noreferrer"
                className="hero-whatsapp"
                dir="ltr"
              >
                📱 03 239 467
              </a>
            </div>
          </div>

          {/* IMAGE */}
          <div className="hero-photo-container">
            <img
              src="/honeyImg.jpeg"
              alt="Honey by Khadija"
              className="hero-photo"
              dir="ltr"
            />
          </div>

          {/* VIDEO */}
          <div className="hero-video-container" style={{ position: "relative" }}>
            <video
              ref={videoRef}
              src="/honeyVd.mp4"
              className="hero-video"
              autoPlay
              loop
              muted={videoMuted}
              playsInline
              style={{ width: "100%" }}
            />
            <button
              onClick={handleVideoToggle}
              style={{
                position: "absolute",
                bottom: 16,
                left: 16,
                zIndex: 10,
                background: "#fff",
                border: "none",
                borderRadius: "50%",
                width: 40,
                height: 40,
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
              aria-label={videoPlaying ? "Pause video" : "Play video"}
            >
              {videoPlaying ? (
                <span style={{ fontSize: 22 }}>⏸️</span>
              ) : (
                <span style={{ fontSize: 22 }}>▶️</span>
              )}
            </button>
            <button
              onClick={handleMuteToggle}
              style={{
                position: "absolute",
                bottom: 16,
                left: 64,
                zIndex: 10,
                background: "#fff",
                border: "none",
                borderRadius: "50%",
                width: 40,
                height: 40,
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
              aria-label={videoMuted ? "Unmute video" : "Mute video"}
            >
              {videoMuted ? (
                <span style={{ fontSize: 22 }}>🔇</span>
              ) : (
                <span style={{ fontSize: 22 }}>🔊</span>
              )}
            </button>
          </div>
        </div>
      </section>
      {/* ================= END HERO ================= */}

      {/* HERO */}
      {/* <section className="hero">
        <div className="left">
          <h2>{t("Home Hero Title")}</h2>
          <p className="muted">{t("Home Hero Subtitle")}</p>

          <div className="actions">
            <button className="btn" onClick={handleSeeAllCountries}>
              {t("Home Browse Countries")}
            </button>

            <button
              className="btn"
              onClick={handleSeeAllCategories}
              style={{
                background: "transparent",
                border: "1px solid #eee",
                color: "#fff"
              }}
            >
              {t("Home Explore Categories")}
            </button>
          </div>
        </div>
      </section> */}

      {/* FEATURED ADS (Top 4) */}
  <section className="featured-ads-section">
  <div className="section-title">
    {/* <h3>{t("Home Featured Title")}</h3> */}
    <span className="muted link-like" onClick={handleSeeAllAds}>
      {t("Home Featured More")}
    </span>
  </div>
  <div className="featured-ads-grid">
    {firstFourAds.map((ad, i) => (
      <div
        key={ad.id || i}
        className="featured-ad-card"
        onClick={() => openAdDetails(ad)}
      >
        <img
          className="featured-ad-image"
          src={ad.imageUrl || DEFAULT_AD_IMAGE}
          alt={ad.title}
        />
        <div className="featured-ad-content">
          <div>
            <div className="featured-ad-title">{ad.title}</div>
            <div className="featured-ad-snippet">{ad.snippet}</div>
            {Number(ad.price) > 0 && (
              <div className="featured-ad-price">{ad.price}</div>
            )}
          </div>
          {ad.phone && (
            <div className="featured-ad-phone-row" onClick={e => e.stopPropagation()}>
              <a
                href={`https://wa.me/${ad.phone.replace(/[^0-9]/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="featured-ad-phone-link"
                title="Contact on WhatsApp"
              >
                <FaWhatsapp style={{ marginRight: 4, fontSize: "1.2em" }} />
                {ad.phone}
              </a>
              <button
                className="featured-ad-copy-btn"
                onClick={() => navigator.clipboard.writeText(ad.phone)}
                title="Copy phone number"
              >
                <FaRegCopy />
              </button>
            </div>
          )}
        </div>
      </div>
    ))}
  </div>
  <div style={{ textAlign: "center", marginTop: 16 }}>
    <button className="btn" onClick={handleSeeAllAds}>
      {t("All Ads", "See All Ads")}
    </button>
  </div>
</section>

      {/* TOP CATEGORY */}
      <section className="page-section">
        <div className="section-title">
          {/* <span className="muted link-like" onClick={handleSeeAllCategories}>
            {t("Home Categories See All")}
          </span> */}
        </div>
        <div className="grid-cats grid-cats-3">
          {topCategories.map(renderCategoryCard)}
        </div>
      </section>

      {/* MORE FEATURED ADS (after first 4, up to 12) */}
      {remainingAds.length > 0 && (
        <section className="page-section">
          <div className="section-title">
            <span className="muted link-like" onClick={handleSeeAllAds}></span>
          </div>

          <div className="featured-row">
            <div className="featured-track" id="featuredTrack">
              {remainingAds.map((ad, i) => (
                <div
                  key={ad.id || i}
                  className="featured-card"
                  onClick={() => openAdDetails(ad)}
                >
                  <div className="ad-thumb">
                    <img
                      src={ad.imageUrl || DEFAULT_AD_IMAGE}
                      alt={ad.title}
                    />
                  </div>
                  <h4 style={{ marginTop: 6 }}>{ad.title}</h4>
                  <p className="muted">{ad.snippet}</p>
                  <strong>{ad.price}</strong>
                </div>
              ))}
            </div>
            <div className="slider-dots" id="sliderDots"></div>
          </div>
        </section>
      )}

      {/* اعلانات مبوبة (Bombed Ads) */}
      {bombedCategories.length > 0 && (
        <section className="page-section">
          <div className="section-title-adsbuttom">
            <h3>{i18n.language === "ar" ? "اعلانات مبوبة" : "Bombed Ads"}</h3>
          </div>
          <div className="grid-cats">
            {bombedCategories.map(renderCategoryCard)}
          </div>
        </section>
      )}

    </main>
  );
};

export default HomePage;